import axios from '@/api/axios-config'
import type { SnowflakeId } from '@/types/snowflake'
import type {
  OwnerReport,
  OwnerReportByTask,
  OwnerReportCompleteInput,
  OwnerReportDecisionInput,
  OwnerReportDraftInput,
  OwnerReportListRow,
  OwnerReportMessage,
  OwnerStayRow,
  OwnerStaySettings,
} from '@/features/owner-portal/types'
import type { OwnerActivitySearchResult, OwnerActivityUnreadCount } from '@/features/owner-portal/activityTypes'
import { refreshCoordinator } from '@/features/realtime/refreshCoordinator'
import type {
  OwnerApprovalDefaults,
  OwnerApprovalDefaultsUpdate,
  OwnerPortalProfile,
  OwnerPortalProfileUpdate,
} from '@/features/owner/types/owner'

/**
 * 后台业主报告接口 `/pms/owner-portal/reports`（设计文档 §5.3）。
 *
 * axios 拦截器已剥掉 `{ code, data, msg }` 外壳，这里直接是 data。
 * 权限码（advisory 门控用，后端 RequirePermission 才是闸门）见 `OWNER_PORTAL_PERMS`。
 */
const BASE = '/pms/owner-portal/reports'

export interface OwnerReportSearchResult {
  results: OwnerReportListRow[]
  pagination: { page: number; pageSize: number; totalResults: number; totalPages?: number }
}

export const ownerPortalApi = {
  searchReports: (data: Record<string, unknown>) =>
    axios.post(`${BASE}/search`, data) as Promise<OwnerReportSearchResult>,
  getReportByTask: (taskId: SnowflakeId) =>
    axios.get(`${BASE}/by-task/${taskId}`) as Promise<OwnerReportByTask>,
  createReport: (taskId: SnowflakeId) =>
    axios.post(BASE, { taskId }) as Promise<OwnerReport>,
  updateReport: (id: SnowflakeId, data: OwnerReportDraftInput) =>
    axios.put(`${BASE}/${id}`, data) as Promise<OwnerReport>,
  publishReport: (id: SnowflakeId) =>
    axios.post(`${BASE}/${id}/publish`, {}) as Promise<OwnerReport>,
  unpublishReport: (id: SnowflakeId) =>
    axios.post(`${BASE}/${id}/unpublish`, {}) as Promise<OwnerReport>,
  recordDecision: (id: SnowflakeId, data: OwnerReportDecisionInput) =>
    axios.post(`${BASE}/${id}/decision`, data) as Promise<OwnerReport>,
  sendMessage: (id: SnowflakeId, body: string) =>
    axios.post(`${BASE}/${id}/messages`, { body }) as Promise<OwnerReportMessage>,
  // 标记已读会改 Owner Activity 的未读数：本页签立刻刷新菜单角标（别的页签 / 员工靠 ownerActivity 实时信号）
  markMessagesRead: (id: SnowflakeId) =>
    axios.post(`${BASE}/${id}/messages/read`, {}).then((res) => {
      refreshCoordinator.invalidate(['ownerActivity'], 'local-write')
      return res
    }),
  completeReport: (id: SnowflakeId, data: OwnerReportCompleteInput) =>
    axios.post(`${BASE}/${id}/complete`, data) as Promise<OwnerReport>,
  cancelReport: (id: SnowflakeId) =>
    axios.post(`${BASE}/${id}/cancel`, {}) as Promise<OwnerReport>,

  // 按业主的门户设置（§5.6）：没有设置行时 GET 返回默认值（hasProfile=false），PUT 首次即建行
  getOwnerProfile: (ownerId: SnowflakeId | string) =>
    axios.get(`/pms/owner-portal/owners/${ownerId}/profile`) as Promise<OwnerPortalProfile>,
  updateOwnerProfile: (ownerId: SnowflakeId | string, data: OwnerPortalProfileUpdate) =>
    axios.put(`/pms/owner-portal/owners/${ownerId}/profile`, data) as Promise<OwnerPortalProfile>,

  // 公司级默认审批设置（配置项 owner_portal.approvals.*；通用 Configuration 页只读，这里是专属入口）。
  // 404（后端还没有这个端点）由 Dialog 自己给可读提示，不弹全局「Resource not found」
  getApprovalDefaults: () =>
    axios.get('/pms/owner-portal/settings/approvals', { suppressNotFoundToast: true } as any) as Promise<OwnerApprovalDefaults>,
  updateApprovalDefaults: (data: OwnerApprovalDefaultsUpdate) =>
    axios.put('/pms/owner-portal/settings/approvals', data, { suppressNotFoundToast: true } as any) as Promise<OwnerApprovalDefaults>,
}

/**
 * 员工端 Owner Activity（跨工单的业主动态）`/pms/owner-portal/activity`。
 * 权限只要 owner_portal:report:read；回复 / 标记已读沿用上面的 sendMessage / markMessagesRead。
 */
const ACTIVITY = '/pms/owner-portal/activity'

export const ownerActivityApi = {
  /** 标准 Search DSL（filters: kind / unread / listingId / ownerId / reportId；orderBy 默认 -at） */
  search: (data: Record<string, unknown>) =>
    axios.post(`${ACTIVITY}/search`, data) as Promise<OwnerActivitySearchResult>,
  /** 员工未读的业主留言数（侧栏角标） */
  unreadCount: () =>
    axios.get(`${ACTIVITY}/unread-count`) as Promise<OwnerActivityUnreadCount>,
}

export interface OwnerStaySearchResult {
  results: OwnerStayRow[]
  pagination: { page: number; pageSize: number; totalResults: number; totalPages?: number }
}

const STAYS = '/pms/owner-portal/owner-stays'

/** 后台业主自住接口 `/pms/owner-portal/owner-stays`（设计文档 §4.13、§6.4）。 */
export const ownerStayApi = {
  search: (data: Record<string, unknown>) =>
    axios.post(`${STAYS}/search`, data) as Promise<OwnerStaySearchResult>,
  get: (id: SnowflakeId) => axios.get(`${STAYS}/${id}`) as Promise<OwnerStayRow>,
  /**
   * 预订详情的业主自住卡片：按预订 id 取门户里的那一单（带业主名）。PMS 里直接建的业主预订没有这一单 → 404，
   * 卡片不显示（不弹「没找到」toast）。
   */
  byReservation: (reservationId: SnowflakeId) =>
    axios.get(`${STAYS}/by-reservation/${reservationId}`, { suppressNotFoundToast: true } as any) as Promise<OwnerStayRow>,
  /** 租户开关（写入 PMS、要不要员工确认、写入白名单、提前期） */
  settings: () => axios.get(`${STAYS}/settings`) as Promise<OwnerStaySettings>,
  /** 确认 / 重试写入 PMS（后台作业去写，返回时还没写完） */
  sync: (id: SnowflakeId) => axios.post(`${STAYS}/${id}/sync`, {}) as Promise<OwnerStayRow>,
  /** 员工已在 PMS 手工封房 */
  markSynced: (id: SnowflakeId, guestyReservationId: string | null) =>
    axios.post(`${STAYS}/${id}/mark-synced`, { guestyReservationId }) as Promise<OwnerStayRow>,
  cancel: (id: SnowflakeId, reason: string | null) =>
    axios.post(`${STAYS}/${id}/cancel`, { reason }) as Promise<OwnerStayRow>,
  /** 员工已在 PMS 手工取消：只在 Nova 作废、不写 PMS（原因必填） */
  cancelLocal: (id: SnowflakeId, reason: string) =>
    axios.post(`${STAYS}/${id}/cancel-local`, { reason }) as Promise<OwnerStayRow>,
}

/** 权限码（设计文档 §5.1，前缀 `owner_portal:`）。 */
export const OWNER_PORTAL_PERMS = {
  reportRead: 'owner_portal:report:read',
  reportCreate: 'owner_portal:report:create',
  reportUpdate: 'owner_portal:report:update',
  reportPublish: 'owner_portal:report:publish',
  reportDecide: 'owner_portal:report:decide',
  reportMessageCreate: 'owner_portal:report_message:create',
  profileRead: 'owner_portal:profile:read',
  profileUpdate: 'owner_portal:profile:update',
  approvalDefaultsRead: 'owner_portal:approval_defaults:read',
  approvalDefaultsUpdate: 'owner_portal:approval_defaults:update',
  stayRead: 'owner_portal:stay:read',
  staySync: 'owner_portal:stay:sync',
} as const
